import * as _ from 'lodash';
import { Reducer } from 'redux';

import { EDITOR_ACTIONS } from '../actions/editor-actions';
import { SchemaTabs } from '../common/types';

interface SelectedVisualization {
  type: 'chart' | 'table' | 'query';
  viewVersion: number;
  token: string;
  queryToken: string;
  chartView?: any;
  tableView?: any;
  queryView?: any;
  bigNumberView?: any;
}

interface SelectedSubVisualization {
  type: 'table-column';

  selectedTableColumn?: string;
}

interface SelectedVisualizationWrapper {
  viz?: SelectedVisualization;
  subViz?: SelectedSubVisualization;
}

export interface EditorState extends SelectedVisualizationWrapper {
  account: boolean;
  chart: boolean;
  chartSidebar: boolean;
  currentReport: any;
  datasetTab: boolean;
  designer: boolean;
  filterPanel: boolean;
  history: boolean;
  html: boolean;
  lists: boolean;
  notebookDataLoading: boolean;
  notebookDataNeedsLoading: boolean;
  notebookState: string;
  notebookStatusBarState: string;
  notebookStatusMessage: string;
  notebookShown: boolean;
  queryMode: boolean;
  report: boolean;
  results: boolean;
  schema: boolean;
  schemaSidebar: boolean;
  schemaTab: SchemaTabs;
  secondarySidebar: boolean;
  selectedQuery: {
    token: string;
    last_run_id: string;
  };
  sql: boolean;
  sqlEditor: boolean;
  table: boolean;
  tableSidebar: boolean;
  vertical: boolean;
  noPreviewRefresh: boolean;
  viz: SelectedVisualization;
  subViz: SelectedSubVisualization;
}

const tableView = (state, action) => {
  switch (action.type) {
    case EDITOR_ACTIONS.SelectTable:
      return {
        table: true,
        tableSidebar: true,
      };
    case EDITOR_ACTIONS.SelectQuery:
    case EDITOR_ACTIONS.SelectChart:
    case EDITOR_ACTIONS.ClearSelected:
      return {
        table: false,
        tableSidebar: false,
      };
  }
};

const chartView = (state, action) => {
  switch (action.type) {
    case EDITOR_ACTIONS.SelectChart:
      return {
        chart: true,
        chartSidebar: true,
      };
    case EDITOR_ACTIONS.SelectQuery:
    case EDITOR_ACTIONS.SelectTable:
    case EDITOR_ACTIONS.ClearSelected:
      return {
        chart: false,
        chartSidebar: false,
      };
  }
};

const selectedQuery = (state, action) => {
  switch (action.type) {
    case EDITOR_ACTIONS.RemapSelectedQuery:
      if (state.selectedQuery && state.selectedQuery.token === action.oldToken) {
        return {
          selectedQuery: {
            token: action.newToken,
            last_run_id: state.selectedQuery.last_run_id,
          },
        };
      }

      return state.selectedQuery;
    case EDITOR_ACTIONS.SelectQuery:
      return {
        selectedQuery: {
          token: action.token,
          last_run_id: action.query ? action.query.last_run_id : null,
        },
      };
    case EDITOR_ACTIONS.SelectChart:
    case EDITOR_ACTIONS.SelectTable:
      return {
        selectedQuery: {
          token: action.query.token,
          last_run_id: action.query.last_run_id,
        },
      };
    case EDITOR_ACTIONS.SelectReport:
      return {
        selectedQuery: {
          token: action.query.token,
          last_run_id: action.query.last_run_id,
        },
      };
  }
};

const selectedVisualization: (state, action) => SelectedVisualizationWrapper = (state, action) => {
  switch (action.type) {
    case EDITOR_ACTIONS.SelectTable:
      return <SelectedVisualizationWrapper>{
        viz: {
          type: 'table',
          tableView: _.cloneDeep(action.model.view),
          queryToken: action.query.token,
          viewVersion: 1,
          token: action.model.token,
        },
      };
    case EDITOR_ACTIONS.SelectChart:
      return {
        viz: {
          type: 'chart',
          chartView: _.cloneDeep(action.model.view),
          viewVersion: action.model.view_version,
          queryToken: action.query.token,
          token: action.model.token,
          color_palette_token: action.model.color_palette_token,
        },
      };
  }
};

const selectedSubVisualization: (state, action) => SelectedVisualizationWrapper = (state, action) => {
  switch (action.type) {
    case EDITOR_ACTIONS.SelectTableColumn:
      return <SelectedVisualizationWrapper>{
        subViz: {
          type: 'table-column',
          selectedTableColumn: action.column,
        },
      };
    case EDITOR_ACTIONS.SelectTable:
      return <SelectedVisualizationWrapper>{
        subViz: null,
      };
    case EDITOR_ACTIONS.SelectChart:
      return <SelectedVisualizationWrapper>{
        subViz: null,
      };
  }
};

const sqlView = (state, action) => {
  switch (action.type) {
    case EDITOR_ACTIONS.SelectChart:
    case EDITOR_ACTIONS.SelectTable:
    case EDITOR_ACTIONS.SelectQuery:
      return {
        sql: !!action.query,
      };

    case EDITOR_ACTIONS.SetView:
      return {
        sql: action.name === 'sql' ? action.show : state.sql,
      };
    case EDITOR_ACTIONS.ToggleMaximize:
      return {
        sql: action.name === 'sql' ? true : !state.sql,
      };
    case EDITOR_ACTIONS.ToggleMinimize:
      return {
        sql: action.name === 'sql' ? !state.sql : true,
      };
  }
};

const sqlEditorView = (state, action) => {
  switch (action.type) {
    case EDITOR_ACTIONS.SelectChart:
    case EDITOR_ACTIONS.SelectTable:
      return {
        sqlEditor: false,
      };

    case EDITOR_ACTIONS.SelectQuery:
      return {
        sqlEditor: !!action.query,
      };

    case EDITOR_ACTIONS.SetView:
      return {
        sqlEditor: action.name === 'sql' ? action.show : state.sqlEditor,
      };
  }
};

const datasetView = (state, action) => {
  switch (action.type) {
    case EDITOR_ACTIONS.SelectQuery:
    case EDITOR_ACTIONS.SelectChart:
    case EDITOR_ACTIONS.SelectTable:
      return {
        datasetTab: false,
      };
  }
};

const htmlView = (state, action) => {
  switch (action.type) {
    case EDITOR_ACTIONS.ToggleMaximize:
      return {
        html: action.name === 'html' ? true : !state.html,
      };
    case EDITOR_ACTIONS.ToggleMinimize:
      return {
        html: action.name === 'html' ? !state.html : true,
      };
  }
};

const resultsView = (state, action) => {
  switch (action.type) {
    case EDITOR_ACTIONS.ToggleMaximize:
      return {
        results: action.name === 'results' ? true : !state.results,
      };
    case EDITOR_ACTIONS.ToggleMinimize:
      return {
        results: action.name === 'results' ? !state.results : true,
      };
    case EDITOR_ACTIONS.SetView:
      return {
        results: action.name === 'results' ? action.show : state.results,
      };
    case EDITOR_ACTIONS.QueryRunComplete:
      return {
        results: action.queryToken === state.selectedQuery.token ? true : state.results,
      };
    case EDITOR_ACTIONS.SelectQuery: // Hide the result window if the user makes a new query
      return {
        results: action.query._new_query ? false : state.results,
      };
  }
};

const designerView = (state, action) => {
  switch (action.type) {
    case EDITOR_ACTIONS.ToggleMaximize:
      return {
        designer: action.name === 'designer' ? true : !state.designer,
      };
    case EDITOR_ACTIONS.ToggleMinimize:
      return {
        designer: action.name === 'designer' ? !state.designer : true,
      };
  }
};

const schemaView = (state, action) => {
  switch (action.type) {
    case EDITOR_ACTIONS.SetView:
      return {
        schema: action.name === 'schema' ? action.show : state.schema,
      };
  }
};

const schemaSidebarView = (state, action) => {
  switch (action.type) {
    case EDITOR_ACTIONS.ToggleSecondarySidebar:
      return {
        // only toggle schema sidebar when toggle secondary sidebar action is called in sql editor state
        schemaSidebar:
          state.queryMode && state.sql && !state.chart && !state.table ? !state.secondarySidebar : state.schemaSidebar,
      };
  }
};

const queryMode = (state, action) => {
  switch (action.type) {
    case EDITOR_ACTIONS.SetQueryMode:
      return {
        queryMode: action.enabled,
      };
    case EDITOR_ACTIONS.SetBuilderMode:
    case EDITOR_ACTIONS.SetNotebookShown:
      return {
        queryMode: false,
      };
    case EDITOR_ACTIONS.SelectQuery:
    case EDITOR_ACTIONS.SelectChart:
    case EDITOR_ACTIONS.SelectTable:
      return {
        queryMode: action.focus ? true : state.queryMode,
      };
    case EDITOR_ACTIONS.SetView:
      if (action.show) {
        switch (action.name) {
          case 'sql':
          case 'results':
            return {
              queryMode: true,
            };
        }
      }
  }
};

const refreshPreview = (state, action) => {
  switch (action.type) {
    case EDITOR_ACTIONS.SetBuilderMode:
      return {
        noPreviewRefresh: action.withoutPreview,
      };
    case EDITOR_ACTIONS.SetQueryMode:
      return {
        noPreviewRefresh: false,
      };
  }
};

const notebookShown = (state, action) => {
  switch (action.type) {
    case EDITOR_ACTIONS.SetNotebookShown:
      return {
        notebookShown: action.shown,
      };
    case EDITOR_ACTIONS.SelectQuery:
    case EDITOR_ACTIONS.SelectChart:
    case EDITOR_ACTIONS.SelectTable:
    case EDITOR_ACTIONS.SetQueryMode:
    case EDITOR_ACTIONS.SetBuilderMode:
      return {
        notebookShown: false,
      };
    case EDITOR_ACTIONS.SetView:
      if (action.name !== 'browser') {
        return {
          notebookShown: false,
        };
      }
  }
};

const secondarySidebarView = (state, action) => {
  switch (action.type) {
    case EDITOR_ACTIONS.SelectQuery:
      return {
        secondarySidebar: state.schemaSidebar,
      };
    case EDITOR_ACTIONS.SelectTable:
    case EDITOR_ACTIONS.SelectChart:
      return {
        secondarySidebar: true,
      };

    case EDITOR_ACTIONS.ToggleSecondarySidebar:
      return {
        secondarySidebar: !state.secondarySidebar,
      };
  }
};

const verticalMode = (state, action) => {
  switch (action.type) {
    case EDITOR_ACTIONS.ToggleVerticalMode:
      return {
        vertical: !state.vertical,
      };
  }
};

const notebookState = (state, action) => {
  switch (action.type) {
    case EDITOR_ACTIONS.NotebookRunning:
      return {
        notebookState: 'running',
        notebookStatusBarState: 'running',
      };
    case EDITOR_ACTIONS.NotebookLoading:
      return {
        notebookState: 'loading',
        notebookStatusBarState: 'loading',
      };
    case EDITOR_ACTIONS.NotebookWarning:
      return {
        notebookState: 'warning',
        notebookStatusBarState: 'warning',
      };
    case EDITOR_ACTIONS.NotebookStartSession:
      return {
        notebookState: 'session',
        notebookStatusBarState: 'loading',
      };
    case EDITOR_ACTIONS.NotebookSessionLost:
      return {
        notebookState: 'error',
        notebookStatusBarState: 'error',
      };
    case EDITOR_ACTIONS.NotebookReady:
      return {
        notebookState: 'ready',
        notebookStatusBarState: 'ready',
      };
    case EDITOR_ACTIONS.NotebookDataOld:
      return {
        notebookState: 'alert',
        notebookStatusBarState: 'alert',
      };
    case EDITOR_ACTIONS.NotebookTerminated:
      return {
        notebookState: 'error',
        notebookStatusBarState: 'error',
      };
  }
};

const notebookStatusMessage = (state, action) => {
  switch (action.type) {
    case EDITOR_ACTIONS.NotebookSessionLost:
      return {
        notebookStatusMessage: 'There was a problem with your session',
      };
    case EDITOR_ACTIONS.NotebookTerminated:
      let message;
      switch (action.reason) {
        case 'CRASHED':
          message = 'Notebook has encounted an unexpected error';
          break;
        case 'EXPIRED':
          message = 'Notebook session ended due to inactivity';
          break;
        case 'OOM':
          message = 'Notebook ran out of memory';
          break;
        default:
          message = 'Notebook session ended';
      }
      return {
        notebookStatusMessage: message,
      };
  }
};

const notebookDataLoading = (state, action) => {
  switch (action.type) {
    case EDITOR_ACTIONS.NotebookDataLoaded:
    case EDITOR_ACTIONS.NotebookSessionLost:
      return {
        notebookDataLoading: false,
      };
    case EDITOR_ACTIONS.NotebookLoading:
      return {
        notebookDataLoading: true,
      };
  }
};

const notebookDataNeedsLoading = (state, action) => {
  switch (action.type) {
    case EDITOR_ACTIONS.NotebookDataLoaded:
      return {
        notebookDataNeedsLoading: false,
      };
    case EDITOR_ACTIONS.ReportRunDataLoaded:
      return {
        notebookDataNeedsLoading: true,
      };
  }
};

const schemaTabs = (state, action) => {
  switch (action.type) {
    case EDITOR_ACTIONS.SelectSchemaTab:
      return {
        schemaTab: action.tab,
      };
  }
};

const filterPanelReducer = (state, action) => {
  switch (action.type) {
    case EDITOR_ACTIONS.OpenFilterPanel:
      return {
        filterPanel: true,
      };
    case EDITOR_ACTIONS.CloseFilterPanel:
      return {
        filterPanel: false,
      };
    case EDITOR_ACTIONS.ToggleFilterPanel:
      return {
        filterPanel: !state.filterPanel,
      };
  }
};

export const EditorReducer: Reducer<EditorState> = (state, action) => {
  const defaults = {
    account: false,
    chart: false,
    chartSidebar: false,
    currentReport: null,
    datasetTab: false,
    designer: true,
    filterPanel: false,
    history: false,
    html: false,
    lists: false,
    notebookDataLoading: false,
    notebookDataNeedsLoading: false,
    notebookState: 'session',
    notebookStatusBarState: 'loading',
    notebookStatusMessage: '',
    notebookShown: false,
    queryMode: true,
    report: false,
    results: true,
    schema: true,
    schemaSidebar: true,
    schemaTab: 'tables',
    secondarySidebar: true,
    sql: true,
    sqlEditor: true,
    table: false,
    tableSidebar: false,
    vertical: true,
    noPreviewRefresh: false,
    viz: {},
  };

  return <EditorState>(
    _.extend(
      defaults,
      state,
      chartView(state, action),
      datasetView(state, action),
      designerView(state, action),
      filterPanelReducer(state, action),
      htmlView(state, action),
      notebookDataLoading(state, action),
      notebookDataNeedsLoading(state, action),
      notebookState(state, action),
      notebookStatusMessage(state, action),
      notebookShown(state, action),
      queryMode(state, action),
      resultsView(state, action),
      schemaSidebarView(state, action),
      schemaTabs(state, action),
      schemaView(state, action),
      secondarySidebarView(state, action),
      selectedQuery(state, action),
      selectedSubVisualization(state, action),
      selectedVisualization(state, action),
      sqlEditorView(state, action),
      sqlView(state, action),
      tableView(state, action),
      verticalMode(state, action),
      refreshPreview(state, action)
    )
  );
};
